import { z } from 'zod'

import {
  fieldItemSnapshotSchema,
  fieldProblemReasonSchema,
  fieldProgressSchema,
  gpsGapKindSchema,
} from './fieldMissions.js'

// Opérations de déneigement et nouveau Centre des opérations (ADR-019, migrations 20261101010000 à 20261101050000) :
// contrats des fonctions serveur de la tablette et du bureau, et règles pures partagées (présence, estimation de
// fin). Aucune dépendance UI, Supabase ou environnementale. Règles : docs/22-Centre-des-operations.md.

const timestampSchema = z.string().refine((value) => !Number.isNaN(Date.parse(value)), 'Horodatage invalide')
const nullableTimestampSchema = timestampSchema.nullable()
const uuidSchema = z.uuid()
const replayedSchema = z.object({ replayed: z.boolean() })

// ------------------------------------------------------------------ Seuils (non mesurés sur le terrain)

/** Battement attendu de la tablette, avec ou sans mission. */
export const OPERATOR_HEARTBEAT_INTERVAL_MS = 30_000 as const
/** Au-delà de ce délai sans battement : « Connexion interrompue » (3 battements manqués). */
export const OPERATOR_ONLINE_MAX_AGE_MS = 90_000 as const
/** Au-delà : la session n'est plus présentée comme active (repère retiré, historique conservé). */
export const OPERATOR_SESSION_EXPIRED_AGE_MS = 15 * 60_000
/** Au-delà : repère grisé « Dernière position reçue il y a… ». */
export const OPERATOR_POSITION_STALE_AGE_MS = 120_000 as const
/** Relecture du centre par curseur. */
export const OPERATIONS_CENTER_POLL_MS = 5_000 as const
/** Au-delà sans relecture réussie : « Connexion de supervision perdue ». */
export const OPERATIONS_CENTER_STALE_MS = 20_000 as const
/** Estimation de fin : volume minimal de résidences déneigées sur la route. */
export const ROUTE_ESTIMATE_MIN_DONE = 8 as const
/** Estimation de fin : plage indicative ± 15 %. */
export const ROUTE_ESTIMATE_SPREAD = 0.15 as const
/** Estimation dégradée : aucune résidence déneigée depuis ce délai alors qu'il en reste. */
export const ROUTE_ESTIMATE_IDLE_MS = 20 * 60_000

// ------------------------------------------------------------------ Énumérations

export const operationStatusSchema = z.enum(['OPEN', 'CLOSED', 'CANCELLED'])
export type OperationStatus = z.infer<typeof operationStatusSchema>

export const operationStateKindSchema = z.enum([
  'ACTIVE',
  'READY_TO_CLOSE',
  'MISSIONS_CLOSED_WITH_EXCEPTIONS',
  'CLOSED',
  'CANCELLED',
])
export type OperationStateKind = z.infer<typeof operationStateKindSchema>

export const missionKindSchema = z.enum(['NORMAL', 'REINFORCEMENT'])
export type MissionKind = z.infer<typeof missionKindSchema>

export const operationTaskStatusSchema = z.enum([
  'TODO',
  'IN_PROGRESS',
  'PROBLEM',
  'DONE',
  'RESOLVED_NO_SERVICE',
])
export type OperationTaskStatus = z.infer<typeof operationTaskStatusSchema>

export const operationTaskStatusLabels: Record<OperationTaskStatus, string> = {
  TODO: 'À faire',
  IN_PROGRESS: 'En cours',
  PROBLEM: 'Problème',
  DONE: 'Déneigée',
  RESOLVED_NO_SERVICE: 'Résolue sans déneigement',
}

export const offerStatusSchema = z.enum(['ACTIVE', 'CLAIMED', 'REVOKED'])
export type OfferStatus = z.infer<typeof offerStatusSchema>

export const helpRequestReasonSchema = z.enum(['BREAKDOWN', 'DELAY', 'DIFFICULT_ACCESS', 'OTHER'])
export type HelpRequestReason = z.infer<typeof helpRequestReasonSchema>

export const helpRequestReasonLabels: Record<HelpRequestReason, string> = {
  BREAKDOWN: 'Panne',
  DELAY: 'Retard',
  DIFFICULT_ACCESS: 'Accès difficile',
  OTHER: 'Autre',
}

export const helpRequestStatusSchema = z.enum(['SENT', 'ACKNOWLEDGED', 'RESOLVED'])
export type HelpRequestStatus = z.infer<typeof helpRequestStatusSchema>

export const conflictKindSchema = z.enum(['COMPLETE_AFTER_TRANSFER', 'PROBLEM_AFTER_TRANSFER'])
export type ConflictKind = z.infer<typeof conflictKindSchema>

// ------------------------------------------------------------------ Tablette

export const heartbeatResultSchema = z.object({
  receivedAt: timestampSchema,
  connection: z.literal('CONNECTED'),
  positionAccepted: z.boolean(),
  availableForHelp: z.boolean(),
})
export type HeartbeatResult = z.infer<typeof heartbeatResultSchema>

export const disconnectResultSchema = z.object({ connection: z.literal('DISCONNECTED'), at: timestampSchema })

export const helpAvailabilityResultSchema = z.object({ availableForHelp: z.boolean() })

export const sendHelpRequestResultSchema = z.object({
  id: uuidSchema,
  status: helpRequestStatusSchema,
  createdAt: timestampSchema,
  replayed: z.boolean(),
})
export type SendHelpRequestResult = z.infer<typeof sendHelpRequestResultSchema>

export const operatorHelpRequestSchema = z.object({
  id: uuidSchema,
  reason: helpRequestReasonSchema,
  comment: z.string().nullable(),
  status: helpRequestStatusSchema,
  createdAt: timestampSchema,
  acknowledgedAt: nullableTimestampSchema,
  acknowledgedByName: z.string().nullable(),
  resolvedAt: nullableTimestampSchema,
})
export type OperatorHelpRequest = z.infer<typeof operatorHelpRequestSchema>
export const operatorHelpRequestsSchema = z.array(operatorHelpRequestSchema)

export const operatorReinforcementOfferSchema = z.object({
  offerId: uuidSchema,
  taskId: uuidSchema,
  taskVersion: z.number().int().positive(),
  status: offerStatusSchema,
  isRetry: z.boolean(),
  operationId: uuidSchema,
  routeId: uuidSchema.nullable(),
  routeName: z.string().nullable(),
  routeColor: z.string().nullable(),
  snapshot: fieldItemSnapshotSchema,
  taskStatus: operationTaskStatusSchema,
  createdAt: timestampSchema,
  claimedAt: nullableTimestampSchema,
  claimedByName: z.string().nullable(),
  /** Nul tant que l'offre n'est pas prise. */
  claimedByMe: z.boolean().nullable(),
  canClaim: z.boolean(),
})
export type OperatorReinforcementOffer = z.infer<typeof operatorReinforcementOfferSchema>

export const operatorReinforcementOffersSchema = z.object({
  missionId: uuidSchema.nullable(),
  missionStatus: z.string().nullable(),
  operationId: uuidSchema.nullable(),
  offers: z.array(operatorReinforcementOfferSchema),
})
export type OperatorReinforcementOffers = z.infer<typeof operatorReinforcementOffersSchema>

export const claimTaskResultSchema = replayedSchema.extend({
  offerId: uuidSchema,
  status: z.literal('CLAIMED'),
  taskId: uuidSchema,
  taskVersion: z.number().int().positive(),
  itemId: uuidSchema,
  missionId: uuidSchema,
  ownerName: z.string().nullable(),
})
export type ClaimTaskResult = z.infer<typeof claimTaskResultSchema>

export const startReinforcementResultSchema = replayedSchema.extend({
  missionId: uuidSchema,
  number: z.number().int().nullable(),
  status: z.literal('IN_PROGRESS'),
  items: z.literal(0),
  operationId: uuidSchema,
  operationNumber: z.string(),
  missionKind: z.literal('REINFORCEMENT'),
})
export type StartReinforcementResult = z.infer<typeof startReinforcementResultSchema>

// ------------------------------------------------------------------ Bureau : lectures

export const operationStateSchema = z.object({
  status: operationStatusSchema,
  activeMissions: z.number().int().nonnegative(),
  unresolvedTasks: z.number().int().nonnegative(),
  problemTasks: z.number().int().nonnegative(),
  activeOffers: z.number().int().nonnegative(),
  openConflicts: z.number().int().nonnegative().optional(),
  state: operationStateKindSchema,
})
export type OperationState = z.infer<typeof operationStateSchema>

export const operationSummarySchema = z.object({
  id: uuidSchema,
  number: z.string(),
  season: z.string(),
  status: operationStatusSchema,
  version: z.number().int().positive(),
  openedAt: timestampSchema,
  closedAt: nullableTimestampSchema,
  closedByName: z.string().nullable(),
  closeNote: z.string().nullable(),
  cancelledAt: nullableTimestampSchema,
  reopenedAt: nullableTimestampSchema,
  reopenedReason: z.string().nullable(),
  stale: z.boolean(),
  firstMissionAt: nullableTimestampSchema,
  lastMissionEndedAt: nullableTimestampSchema,
  state: operationStateSchema,
})
export type OperationSummary = z.infer<typeof operationSummarySchema>

/** Compteurs sur tâches uniques : Problèmes ⊂ Restantes (jamais additionnés). */
export const operationCountersSchema = z.object({
  activeMissions: z.number().int().nonnegative(),
  pausedMissions: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  remaining: z.number().int().nonnegative(),
  problems: z.number().int().nonnegative(),
  done: z.number().int().nonnegative(),
  resolvedNoService: z.number().int().nonnegative(),
  toReassign: z.number().int().nonnegative(),
})
export type OperationCounters = z.infer<typeof operationCountersSchema>

export const operationRouteSchema = z.object({
  routeId: uuidSchema.nullable(),
  name: z.string().nullable(),
  color: z.string().nullable(),
  total: z.number().int().nonnegative(),
  done: z.number().int().nonnegative(),
  remaining: z.number().int().nonnegative(),
  problems: z.number().int().nonnegative(),
  resolvedNoService: z.number().int().nonnegative(),
  offered: z.number().int().nonnegative(),
  startedAt: nullableTimestampSchema,
  doneTimes: z.array(timestampSchema),
  missions: z.array(
    z.object({
      id: uuidSchema,
      operatorName: z.string().nullable(),
      status: z.string(),
      kind: missionKindSchema,
    }),
  ),
})
export type OperationRoute = z.infer<typeof operationRouteSchema>

export const operationTaskSchema = z.object({
  id: uuidSchema,
  version: z.number().int().positive(),
  status: operationTaskStatusSchema,
  passNumber: z.number().int().positive(),
  serviceLocationId: uuidSchema,
  routeId: uuidSchema.nullable(),
  routeName: z.string().nullable(),
  routeColor: z.string().nullable(),
  routeOrder: z.number().nullable(),
  snapshot: fieldItemSnapshotSchema,
  ownerMissionId: uuidSchema.nullable(),
  ownerEmployeeId: uuidSchema.nullable(),
  ownerName: z.string().nullable(),
  ownerMissionOpen: z.boolean(),
  needsReassignment: z.boolean(),
  retryRequestedAt: nullableTimestampSchema,
  /** Connu de la tablette (journal de détection) seulement : jamais déduit par le centre. */
  inProgress: z.boolean(),
  doneAt: nullableTimestampSchema,
  resolutionReason: z.string().nullable(),
  updatedAt: timestampSchema,
  attemptCount: z.number().int().nonnegative(),
  lastProblem: z
    .object({
      reasonCode: fieldProblemReasonSchema.nullable(),
      reasonText: z.string().nullable(),
      note: z.string().nullable(),
      at: timestampSchema,
      operatorName: z.string().nullable(),
    })
    .nullable(),
  offer: z
    .object({
      id: uuidSchema,
      status: offerStatusSchema,
      isRetry: z.boolean(),
      createdAt: timestampSchema,
      claimedAt: nullableTimestampSchema,
      claimedByName: z.string().nullable(),
    })
    .nullable(),
})
export type OperationTask = z.infer<typeof operationTaskSchema>

export const operatorPresenceSchema = z.object({
  connection: z.enum(['CONNECTED', 'DISCONNECTED']),
  connectedAt: timestampSchema,
  lastHeartbeatAt: timestampSchema,
  disconnectedAt: nullableTimestampSchema,
  availableForHelp: z.boolean(),
  availableSince: nullableTimestampSchema,
})

export const operatorPositionSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
  accuracy: z.number().nullable(),
  heading: z.number().nullable(),
  capturedAt: timestampSchema,
  receivedAt: timestampSchema,
  source: z.enum(['HEARTBEAT', 'MISSION']),
})
export type OperatorPosition = z.infer<typeof operatorPositionSchema>

export const centerOperatorSchema = z.object({
  employeeId: uuidSchema,
  name: z.string().nullable(),
  assignedEquipmentLabel: z.string().nullable(),
  assignedRouteName: z.string().nullable(),
  presence: operatorPresenceSchema.nullable(),
  position: operatorPositionSchema.nullable(),
  mission: z
    .object({
      id: uuidSchema,
      kind: missionKindSchema,
      status: z.enum(['IN_PROGRESS', 'PAUSED']),
      operationId: uuidSchema.nullable(),
      routeId: uuidSchema,
      routeName: z.string().nullable(),
      routeColor: z.string().nullable(),
      equipmentLabel: z.string().nullable(),
      startedAt: timestampSchema,
      pausedAt: nullableTimestampSchema,
      startEngineHours: z.number().nonnegative(),
      progress: fieldProgressSchema,
    })
    .nullable(),
  lastClosedMission: z
    .object({
      id: uuidSchema,
      kind: missionKindSchema,
      status: z.enum(['COMPLETED', 'CANCELLED']),
      routeName: z.string().nullable(),
      endedAt: nullableTimestampSchema,
      cancelKind: z.enum(['ACCIDENTAL_START', 'ADMIN']).nullable(),
    })
    .nullable(),
  openHelpRequest: z
    .object({
      id: uuidSchema,
      reason: helpRequestReasonSchema,
      status: helpRequestStatusSchema,
      createdAt: timestampSchema,
    })
    .nullable(),
})
export type CenterOperator = z.infer<typeof centerOperatorSchema>

export const centerHelpRequestSchema = z.object({
  id: uuidSchema,
  reason: helpRequestReasonSchema,
  comment: z.string().nullable(),
  status: helpRequestStatusSchema,
  createdAt: timestampSchema,
  employeeId: uuidSchema,
  missionId: uuidSchema.nullable(),
  operatorName: z.string().nullable(),
  acknowledgedAt: nullableTimestampSchema,
  acknowledgedByName: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
})
export type CenterHelpRequest = z.infer<typeof centerHelpRequestSchema>

export const centerConflictSchema = z.object({
  id: uuidSchema,
  kind: conflictKindSchema,
  taskId: uuidSchema,
  missionId: uuidSchema.nullable(),
  createdAt: timestampSchema,
  payload: z.record(z.string(), z.unknown()),
  operatorName: z.string().nullable(),
  address: z.string().nullable(),
})
export type CenterConflict = z.infer<typeof centerConflictSchema>

export const watchAckSchema = z.object({
  incidentKey: z.string(),
  ackedAt: timestampSchema,
  ackedByName: z.string().nullable(),
})
export type WatchAck = z.infer<typeof watchAckSchema>

export const operationEventSchema = z.object({
  id: z.number().int().positive(),
  kind: z.string(),
  at: timestampSchema,
  operationId: uuidSchema.nullable(),
  missionId: uuidSchema.nullable(),
  taskId: uuidSchema.nullable(),
  actorName: z.string().nullable(),
  payload: z.record(z.string(), z.unknown()),
})
export type OperationEvent = z.infer<typeof operationEventSchema>

export const operationsCenterSnapshotSchema = z.object({
  serverTime: timestampSchema,
  cursor: z.number().int().nonnegative(),
  timezone: z.string(),
  operation: operationSummarySchema.nullable(),
  counters: operationCountersSchema.nullable(),
  routes: z.array(operationRouteSchema),
  tasks: z.array(operationTaskSchema),
  operators: z.array(centerOperatorSchema),
  helpRequests: z.array(centerHelpRequestSchema),
  conflicts: z.array(centerConflictSchema),
  acks: z.array(watchAckSchema),
  journal: z.array(operationEventSchema),
})
export type OperationsCenterSnapshot = z.infer<typeof operationsCenterSnapshotSchema>

export const operationsCenterChangesSchema = z.object({
  serverTime: timestampSchema,
  resync: z.boolean(),
  cursor: z.number().int().nonnegative(),
  events: z.array(operationEventSchema),
  operationId: uuidSchema.nullable().optional(),
  operators: z.array(centerOperatorSchema).optional(),
})
export type OperationsCenterChanges = z.infer<typeof operationsCenterChangesSchema>

export const operationTrackPointSchema = z.object({
  id: uuidSchema,
  capturedAt: timestampSchema,
  receivedAt: timestampSchema,
  latitude: z.number(),
  longitude: z.number(),
  accuracy: z.number().nullable(),
})

export const operationTracksSchema = z.object({
  operationId: uuidSchema,
  serverTime: timestampSchema,
  missions: z.array(
    z.object({
      missionId: uuidSchema,
      employeeId: uuidSchema.nullable(),
      equipmentId: uuidSchema.nullable(),
      equipmentLabel: z.string().nullable(),
      operatorName: z.string().nullable(),
      kind: missionKindSchema,
      status: z.string(),
      startedAt: timestampSchema,
      endedAt: nullableTimestampSchema,
      points: z.array(operationTrackPointSchema),
      pauses: z.array(z.object({ startedAt: timestampSchema, endedAt: nullableTimestampSchema })),
      gaps: z.array(
        z.object({ kind: gpsGapKindSchema, startedAt: timestampSchema, endedAt: nullableTimestampSchema }),
      ),
    }),
  ),
})
export type OperationTracks = z.infer<typeof operationTracksSchema>

export const operationStatsSchema = z.object({
  operationId: uuidSchema,
  serverTime: timestampSchema,
  firstMissionAt: nullableTimestampSchema,
  lastMissionEndedAt: nullableTimestampSchema,
  closedAt: nullableTimestampSchema,
  elapsedSeconds: z.number().int().nonnegative().nullable(),
  activeMissions: z.number().int().nonnegative(),
  tasks: operationCountersSchema,
  routes: z.array(
    z.object({
      routeId: uuidSchema.nullable(),
      name: z.string().nullable(),
      color: z.string().nullable(),
      total: z.number().int().nonnegative(),
      done: z.number().int().nonnegative(),
      remaining: z.number().int().nonnegative(),
      problems: z.number().int().nonnegative(),
      resolvedNoService: z.number().int().nonnegative(),
      doneByReinforcement: z.number().int().nonnegative(),
      retries: z.number().int().nonnegative(),
    }),
  ),
  missions: z.array(
    z.object({
      missionId: uuidSchema,
      employeeId: uuidSchema.nullable(),
      operatorName: z.string().nullable(),
      equipmentId: uuidSchema.nullable(),
      equipmentLabel: z.string().nullable(),
      kind: missionKindSchema,
      routeName: z.string().nullable(),
      status: z.string(),
      cancelKind: z.enum(['ACCIDENTAL_START', 'ADMIN']).nullable(),
      startedAt: timestampSchema,
      endedAt: nullableTimestampSchema,
      pausedSeconds: z.number().nonnegative(),
      pauseCount: z.number().int().nonnegative(),
      startEngineHours: z.number().nonnegative(),
      endEngineHours: z.number().nonnegative().nullable(),
      done: z.number().int().nonnegative(),
      doneOtherRoutes: z.number().int().nonnegative(),
      problemsReported: z.number().int().nonnegative(),
      reinforcementReceived: z.number().int().nonnegative(),
      helpRequests: z.number().int().nonnegative(),
      gpsPoints: z.number().int().nonnegative(),
      gpsGaps: z.number().int().nonnegative(),
      lastPositionReceivedAt: nullableTimestampSchema,
    }),
  ),
  helpRequests: z.number().int().nonnegative(),
  offersPublished: z.number().int().nonnegative(),
  offersClaimed: z.number().int().nonnegative(),
})
export type OperationStats = z.infer<typeof operationStatsSchema>

export const operationTaskDetailSchema = operationTaskSchema.extend({
  operationId: uuidSchema,
  clientContractId: uuidSchema.nullable(),
  attempts: z.array(
    z.object({
      id: uuidSchema,
      result: z.enum([
        'DONE',
        'PROBLEM',
        'REOPENED',
        'RELEASED',
        'RETRY_REQUESTED',
        'RESOLVED_NO_SERVICE',
        'RESET_TODO',
        'CONFLICT',
      ]),
      reasonCode: z.string().nullable(),
      reasonText: z.string().nullable(),
      note: z.string().nullable(),
      source: z.string().nullable(),
      at: timestampSchema,
      missionId: uuidSchema.nullable(),
      operatorName: z.string().nullable(),
      actorName: z.string().nullable(),
    }),
  ),
  conflicts: z.array(
    z.object({
      id: uuidSchema,
      kind: conflictKindSchema,
      createdAt: timestampSchema,
      resolvedAt: nullableTimestampSchema,
      resolutionNote: z.string().nullable(),
      operatorName: z.string().nullable(),
    }),
  ),
})
export type OperationTaskDetail = z.infer<typeof operationTaskDetailSchema>

export const operationTasksSchema = z.array(operationTaskSchema)

export const operationsListSchema = z.object({
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  total: z.number().int().nonnegative(),
  rows: z.array(
    z.object({
      id: uuidSchema,
      number: z.string(),
      season: z.string(),
      status: operationStatusSchema,
      openedAt: timestampSchema,
      closedAt: nullableTimestampSchema,
      cancelledAt: nullableTimestampSchema,
      missions: z.number().int().nonnegative(),
      counters: operationCountersSchema,
    }),
  ),
})
export type OperationsList = z.infer<typeof operationsListSchema>

// ------------------------------------------------------------------ Bureau : commandes

export const operationTaskCommandResultSchema = replayedSchema.extend({
  taskId: uuidSchema,
  status: operationTaskStatusSchema,
  version: z.number().int().positive(),
  ownerMissionId: uuidSchema.nullable(),
  needsReassignment: z.boolean(),
  retryRequestedAt: nullableTimestampSchema,
  resolutionReason: z.string().nullable(),
  itemId: uuidSchema.optional(),
})
export type OperationTaskCommandResult = z.infer<typeof operationTaskCommandResultSchema>

export const operationCommandResultSchema = replayedSchema.extend({
  operationId: uuidSchema,
  status: operationStatusSchema,
  version: z.number().int().positive(),
  closedAt: nullableTimestampSchema.optional(),
})
export type OperationCommandResult = z.infer<typeof operationCommandResultSchema>

export const publishOfferResultSchema = replayedSchema.extend({
  operationId: uuidSchema,
  offers: z.array(z.object({ offerId: uuidSchema, taskId: uuidSchema })),
  allEligible: z.boolean(),
  recipients: z.array(uuidSchema),
})
export type PublishOfferResult = z.infer<typeof publishOfferResultSchema>

export const revokeOfferResultSchema = replayedSchema.extend({
  offerId: uuidSchema,
  status: offerStatusSchema,
})

export const helpRequestCommandResultSchema = replayedSchema.extend({
  id: uuidSchema,
  status: helpRequestStatusSchema,
  acknowledgedAt: nullableTimestampSchema.optional(),
  acknowledgedBy: uuidSchema.nullable().optional(),
  resolvedAt: nullableTimestampSchema.optional(),
})

export const watchAckResultSchema = z.object({
  incidentKey: z.string(),
  ackedAt: timestampSchema,
  ackedBy: uuidSchema,
})

export const conflictResolveResultSchema = replayedSchema.extend({
  conflictId: uuidSchema,
  resolvedAt: timestampSchema,
})

// ------------------------------------------------------------------ Règles pures

export type ConnectionState = 'ONLINE' | 'INTERRUPTED' | 'EXPIRED' | 'DISCONNECTED' | 'NEVER'
export type PositionState = 'RECENT' | 'STALE' | 'NONE'
export type WorkState = 'MISSION' | 'PAUSED' | 'AVAILABLE' | 'NONE'

export interface DerivedPresence {
  connection: ConnectionState
  /** Âge du dernier battement (ms), ou null s'il n'y en a jamais eu. */
  heartbeatAgeMs: number | null
  position: PositionState
  positionAgeMs: number | null
  work: WorkState
  /** Repère sur la carte : seulement avec une position reçue, et jamais après une déconnexion explicite. */
  showMarker: boolean
}

/**
 * Connexion, fraîcheur GPS et travail sont indépendants : une mission fermée ne déconnecte pas, une coupure ne ferme
 * pas la mission, un GPS immobile n'est jamais une pause. `serverNowMs` = heure du serveur (jamais celle de
 * l'appareil) corrigée du temps écoulé depuis la lecture.
 */
export function derivePresence(operator: CenterOperator, serverNowMs: number): DerivedPresence {
  const presence = operator.presence
  const heartbeatAgeMs = presence ? Math.max(0, serverNowMs - Date.parse(presence.lastHeartbeatAt)) : null
  let connection: ConnectionState
  if (!presence) connection = 'NEVER'
  else if (presence.connection === 'DISCONNECTED') connection = 'DISCONNECTED'
  else if (heartbeatAgeMs !== null && heartbeatAgeMs <= OPERATOR_ONLINE_MAX_AGE_MS) connection = 'ONLINE'
  else if (heartbeatAgeMs !== null && heartbeatAgeMs <= OPERATOR_SESSION_EXPIRED_AGE_MS)
    connection = 'INTERRUPTED'
  else connection = 'EXPIRED'

  const positionAgeMs = operator.position
    ? Math.max(0, serverNowMs - Date.parse(operator.position.capturedAt))
    : null
  const position: PositionState =
    positionAgeMs === null ? 'NONE' : positionAgeMs <= OPERATOR_POSITION_STALE_AGE_MS ? 'RECENT' : 'STALE'

  const work: WorkState = operator.mission
    ? operator.mission.status === 'PAUSED'
      ? 'PAUSED'
      : 'MISSION'
    : presence?.availableForHelp && connection !== 'DISCONNECTED'
      ? 'AVAILABLE'
      : 'NONE'

  // Une mission ouverte garde son repère (grisé) pendant une coupure; sans mission, une session expirée le retire.
  const showMarker =
    position !== 'NONE' &&
    connection !== 'DISCONNECTED' &&
    (connection !== 'EXPIRED' || operator.mission !== null) &&
    (connection !== 'NEVER' || operator.mission !== null)
  return { connection, heartbeatAgeMs, position, positionAgeMs, work, showMarker }
}

export type RouteEstimate =
  | { kind: 'DONE' }
  | { kind: 'ESTIMATE'; earliest: string; latest: string; pacePerHour: number }
  | { kind: 'INSUFFICIENT'; reason: 'NOT_STARTED' | 'TOO_FEW' | 'IDLE' | 'BLOCKED' }

/**
 * Fin estimée d'une route : rythme observé depuis le premier départ (résidences déneigées par heure), appliqué aux
 * restantes; plage indicative ± 15 %. Jamais une heure précise. Données insuffisantes sans volume minimal, pendant
 * une interruption (aucune résidence depuis 20 min) ou si toutes les restantes sont bloquées par des problèmes.
 */
export function estimateRouteFinish(input: {
  startedAt: string | null
  doneTimes: string[]
  remaining: number
  problems: number
  nowMs: number
}): RouteEstimate {
  if (input.remaining === 0) return { kind: 'DONE' }
  if (!input.startedAt) return { kind: 'INSUFFICIENT', reason: 'NOT_STARTED' }
  if (input.remaining === input.problems) return { kind: 'INSUFFICIENT', reason: 'BLOCKED' }
  const done = input.doneTimes.length
  if (done < ROUTE_ESTIMATE_MIN_DONE) return { kind: 'INSUFFICIENT', reason: 'TOO_FEW' }
  const last = Math.max(...input.doneTimes.map((time) => Date.parse(time)))
  if (input.nowMs - last > ROUTE_ESTIMATE_IDLE_MS) return { kind: 'INSUFFICIENT', reason: 'IDLE' }
  const elapsedMs = input.nowMs - Date.parse(input.startedAt)
  if (elapsedMs <= 0) return { kind: 'INSUFFICIENT', reason: 'TOO_FEW' }
  const pacePerMs = done / elapsedMs
  const remainingMs = input.remaining / pacePerMs
  return {
    kind: 'ESTIMATE',
    earliest: new Date(input.nowMs + remainingMs * (1 - ROUTE_ESTIMATE_SPREAD)).toISOString(),
    latest: new Date(input.nowMs + remainingMs * (1 + ROUTE_ESTIMATE_SPREAD)).toISOString(),
    pacePerHour: Math.round(pacePerMs * 3_600_000 * 10) / 10,
  }
}

/** Clés d'incident de la file À surveiller : une alerte par incident, jamais recréée à chaque relecture. */
export const watchIncidentKey = {
  task: (taskId: string, attemptCount: number) => `task:${taskId}:${String(attemptCount)}`,
  signal: (employeeId: string, lastHeartbeatAt: string) =>
    `signal:${employeeId}:${String(Date.parse(lastHeartbeatAt))}`,
  conflict: (conflictId: string) => `conflict:${conflictId}`,
  offer: (offerId: string) => `offer:${offerId}`,
}
